import { deepCloneObject } from "@thekilroygroup/shared-ui";
import {
  IAirline,
  IFilters,
  ILightItinerary,
  ILightItineraryTimeRange,
} from "redux/filters";
import { ISortBy, initialIndexes } from "redux/sort";

export const getFilteredIndexes = (
  lightItineraries: ILightItinerary[],
  filters: IFilters,
  indexes: ISortBy,
  fareId: string | null,
) => {
  /**
   *
   * @param lightItinerary If an itinerary has a paired itinerary we hide the one with baggage. The one without will show the add baggage button if available.
   */
  const filterDuplicates = (lightItinerary: ILightItinerary) => {
    if (!lightItinerary.isDuplicate) {
      return true;
    } else {
      const filterBaggage = filters.baggageAllowance;
      const checkedBaggage = lightItinerary.checkedBaggage;

      const showAllFilter =
        (filterBaggage.included && filterBaggage.notIncluded) ||
        (!filterBaggage.included && !filterBaggage.notIncluded);
      const showIncludedFilter =
        filterBaggage.included && !filterBaggage.notIncluded;
      const showNotIncludedFilter =
        !filterBaggage.included && filterBaggage.notIncluded;

      return (
        (showAllFilter && !checkedBaggage) ||
        (showIncludedFilter && checkedBaggage) ||
        (showNotIncludedFilter && !checkedBaggage)
      );
    }
  };

  const filterBaggageAllowance = (lightItinerary: ILightItinerary) => {
    const filterBaggage = filters.baggageAllowance;
    const checkedBaggage = lightItinerary.checkedBaggage;
    if (!filters.baggageAllowance.touched) return true;
    else if (
      checkedBaggage &&
      filterBaggage.included &&
      !filterBaggage.notIncluded
    )
      return true;
    else if (
      !checkedBaggage &&
      !filterBaggage.included &&
      filterBaggage.notIncluded
    )
      return true;
    else if (
      (filterBaggage.included && filterBaggage.notIncluded) ||
      (!filterBaggage.included && !filterBaggage.notIncluded)
    )
      return true;
    else return false;
  };

  const filterTravellerTypes = (lightItinerary: ILightItinerary) => {
    const filterTravellerType = filters.travellerTypes;
    const travellerType = lightItinerary.travellerType;

    if (
      !filterTravellerType.touched &&
      !filterTravellerType.ordinary &&
      !filterTravellerType.studentsAndYouth
    ) {
      return true;
    } else if (filterTravellerType.ordinary && travellerType === "ORDINARY") {
      return true;
    } else if (
      filterTravellerType.studentsAndYouth &&
      travellerType === "YOUTHSTUDENT"
    ) {
      return true;
    }

    return false;
  };

  const filterStops = (lightItinerary: ILightItinerary) => {
    if (!filters.stops.touched) return true;
    const stops = lightItinerary.stops;
    const filter = filters.stops;

    if (stops >= 2 && filter.twoPlusStops) {
      return true;
    } else if (stops === 1 && filter.oneStop) {
      return true;
    } else if (
      stops === 0 &&
      (filter.direct || (!filter.oneStop && !filter.twoPlusStops))
    ) {
      return true;
    }
    return false;
  };

  const filterMaxDuration = (lightItinerary: ILightItinerary) => {
    if (!filters.maxDurations.touched) return true;
    return lightItinerary.duration.every((duration: number, i: number) => {
      return duration <= filters.maxDurations.maxDurations[i];
    });
  };

  const filterDepartureArrivalTime = (lightItinerary: ILightItinerary) => {
    if (!filters.departureTime?.touched) return true;
    return lightItinerary.departureTime?.every(
      (flight: ILightItineraryTimeRange, i: number) => {
        return (
          flight.departure >=
            filters.departureTime.departureTimeRanges[i]?.departure[0] &&
          flight.departure <=
            filters.departureTime.departureTimeRanges[i]?.departure[1] &&
          flight.arrival >=
            filters.departureTime.departureTimeRanges[i]?.arrival[0] &&
          flight.arrival <=
            filters.departureTime.departureTimeRanges[i]?.arrival[1]
        );
      },
    );
  };

  const filterAirlines = (lightItinerary: ILightItinerary) => {
    if (!filters.preferredAirlines.touched) return true;
    return filters.preferredAirlines.airlines.some(
      (airline: IAirline, i: number) => {
        return lightItinerary.airlines.includes(airline.id);
      },
    );
  };

  const filteredOutIndexes: number[] = [];

  lightItineraries.forEach((lightItinerary, i) => {
    if (
      !filterDuplicates(lightItinerary) ||
      !filterStops(lightItinerary) ||
      !filterBaggageAllowance(lightItinerary) ||
      !filterTravellerTypes(lightItinerary) ||
      !filterMaxDuration(lightItinerary) ||
      !filterDepartureArrivalTime(lightItinerary) ||
      !filterAirlines(lightItinerary)
    ) {
      filteredOutIndexes.push(i);
    }
  });

  if (filteredOutIndexes.length === 0) {
    return addSelectedFareId(fareId, lightItineraries, indexes);
  }

  if (filteredOutIndexes.length === lightItineraries.length) {
    return addSelectedFareId(fareId, lightItineraries, initialIndexes);
  } else {
    const { penaltyPoints, price, flightTime } = indexes;
    const flightTimeFiltered = flightTime.filter(
      (index) => !filteredOutIndexes.includes(index),
    );
    const priceFiltered = price.filter(
      (index) => !filteredOutIndexes.includes(index),
    );
    const penaltyPointsFiltered = penaltyPoints.filter(
      (index) => !filteredOutIndexes.includes(index),
    );

    let nextIndexes: ISortBy = {
      penaltyPoints: penaltyPointsFiltered,
      price: priceFiltered,
      flightTime: flightTimeFiltered,
    };

    return addSelectedFareId(fareId, lightItineraries, nextIndexes);
  }
};

const addSelectedFareId = (
  fareId: string | null,
  lightItineraries: ILightItinerary[],
  indexes: ISortBy,
) => {
  const fareIdIndex = lightItineraries.findIndex(
    (obj: ILightItinerary) => obj.id === fareId,
  );
  let nextIndexes: ISortBy = deepCloneObject(indexes);
  if (fareIdIndex > -1) {
    for (const key in nextIndexes) {
      if (nextIndexes.hasOwnProperty(key)) {
        let array = nextIndexes[key as keyof ISortBy];
        nextIndexes[key as keyof ISortBy] = [
          fareIdIndex,
          ...array.filter((val) => val !== fareIdIndex),
        ];
      }
    }
  }
  return nextIndexes;
};
